// God-mode design kit — light, enterprise, indigo (#2f33d6) to match the login page.
// Every god page is built only from these pieces so the panel reads as one product.
// Styles live in <KitStyles/> (rendered once by the shell); classes are `k-` prefixed.
import { createContext, useContext, useEffect, useState } from "react";
import { Check, ChevronLeft, ChevronRight, Copy, Inbox, Search, X } from "lucide-react";
import { ago, copyText, inr, inrCompact, shortId, when } from "../components/sc/ui";

export { ago, inr, inrCompact, shortId, when };

// ─────────────────────────── navigation ───────────────────────────
export const NavContext = createContext({ path: "overview", go: () => {} });
export const useNav = () => useContext(NavContext);

// ─────────────────────────── layout ───────────────────────────
export function Page({ children }) {
  return <div className="k-page">{children}</div>;
}

export function Grid({ cols = 4, children, style }) {
  return <div className={`k-grid k-grid-${cols}`} style={style}>{children}</div>;
}

export function Section({ title, sub, icon: Icon, actions, children, pad = true, footer, className = "", testId }) {
  return (
    <section className={`k-section ${className}`} data-testid={testId}>
      {(title || actions) && (
        <header className="k-section-head">
          <div className="k-section-title">
            {Icon && <span className="k-section-icon"><Icon size={16} strokeWidth={2.1} /></span>}
            <div style={{ minWidth: 0 }}>
              {title && <h3>{title}</h3>}
              {sub && <p>{sub}</p>}
            </div>
          </div>
          {actions && <div className="k-section-actions">{actions}</div>}
        </header>
      )}
      <div className={pad ? "k-section-body" : ""}>{children}</div>
      {footer && <footer className="k-section-foot">{footer}</footer>}
    </section>
  );
}

// ─────────────────────────── numbers ───────────────────────────
export function Kpi({ label, value, sub, icon: Icon, hero, onClick, testId }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag className={`k-kpi ${hero ? "is-hero" : ""} ${onClick ? "is-click" : ""}`} onClick={onClick} data-testid={testId}>
      <div className="k-kpi-top">
        <span className="k-kpi-label">{label}</span>
        {Icon && <span className="k-kpi-icon"><Icon size={16} strokeWidth={2} /></span>}
      </div>
      <div className="k-kpi-value">{value}</div>
      {sub != null && <div className="k-kpi-sub">{sub}</div>}
    </Tag>
  );
}

// ─────────────────────────── status ───────────────────────────
const TONE = {
  green: ["#0f7a45", "#E7F6EE"], red: ["#c5221f", "#FDECEA"], amber: ["#a15c00", "#FFF4E0"],
  blue: ["#2f33d6", "#ECEDFE"], gray: ["#5b6170", "#F0F2F6"], violet: ["#6b3fd4", "#F1ECFD"],
};
export function Pill({ tone = "gray", dot, children, title }) {
  const [fg, bg] = TONE[tone] || TONE.gray;
  return <span className="k-pill" style={{ color: fg, background: bg }} title={title}>{dot && <i style={{ background: fg }} />}{children}</span>;
}

const STATUS = {
  success: ["green", "Success"], settled: ["green", "Settled"], paid: ["green", "Paid"], delivered: ["green", "Delivered"], active: ["green", "Active"],
  manually_confirmed: ["green", "Confirmed"],
  pending: ["amber", "Pending"], initiated: ["blue", "Initiated"], creating: ["blue", "Creating"], awaiting_payment: ["blue", "Awaiting"],
  retrying: ["amber", "Retrying"], review: ["amber", "In review"], dispute: ["violet", "Dispute"], sending: ["blue", "Sending"],
  failed: ["red", "Failed"], error: ["red", "Error"], dropped: ["gray", "Dropped"], expired: ["gray", "Expired"],
  suspended: ["red", "Suspended"], revoked: ["gray", "Revoked"], no_url: ["gray", "No URL"], disabled: ["gray", "Disabled"],
};
export function StatusPill({ status, label }) {
  const [tone, text] = STATUS[status] || ["gray", status || "—"];
  return <Pill tone={tone} dot>{label || text}</Pill>;
}

export function Health({ ok, label }) {
  return <span className={`k-health ${ok ? "is-ok" : "is-bad"}`}><i />{label}</span>;
}

// ─────────────────────────── key/value ───────────────────────────
// items: [[label, value, { mono, copy, wrap }]] — empty values render "—" unless hideEmpty.
export function Rows({ items, hideEmpty }) {
  const shown = items.filter(Boolean).filter(([, v]) => !hideEmpty || (v != null && v !== ""));
  return (
    <div className="k-rows">
      {shown.map(([k, v, o = {}]) => (
        <div key={k} className="k-row">
          <span className="k-row-k">{k}</span>
          <span className={`k-row-v ${o.mono ? "is-mono" : ""}`}>
            {v == null || v === "" ? <span className="k-faint">—</span> : v}
            {o.copy && v ? <CopyBtn value={typeof o.copy === "string" ? o.copy : v} /> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

export function CopyBtn({ value, label }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <button type="button" className="k-copy" aria-label="Copy" title="Copy"
      onClick={async (e) => { e.stopPropagation(); await copyText(String(value), label); setDone(true); setTimeout(() => setDone(false), 1200); }}>
      {done ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

// ─────────────────────────── controls ───────────────────────────
export function Button({ variant = "secondary", size, icon: Icon, children, className = "", ...rest }) {
  return (
    <button type="button" className={`k-btn k-btn-${variant} ${size === "sm" ? "k-btn-sm" : ""} ${className}`} {...rest}>
      {Icon && <Icon size={size === "sm" ? 14 : 16} strokeWidth={2.1} />}
      {children}
    </button>
  );
}

export function Field({ label, hint, children, wide }) {
  return (
    <label className={`k-field ${wide ? "is-wide" : ""}`}>
      {label && <span className="k-field-label">{label}</span>}
      {children}
      {hint && <span className="k-field-hint">{hint}</span>}
    </label>
  );
}

export function Input({ mono, className = "", ...rest }) {
  return <input className={`k-input ${mono ? "is-mono" : ""} ${className}`} {...rest} />;
}

export function Textarea({ mono, className = "", ...rest }) {
  return <textarea className={`k-input k-textarea ${mono ? "is-mono" : ""} ${className}`} {...rest} />;
}

export function Select({ options, className = "", ...rest }) {
  return (
    <select className={`k-input k-select ${className}`} {...rest}>
      {options.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>))}
    </select>
  );
}

export function SearchInput({ value, onChange, placeholder = "Search…", testId }) {
  return (
    <div className="k-search">
      <Search size={16} />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
      {value && <button type="button" onClick={() => onChange("")} aria-label="Clear"><X size={14} /></button>}
    </div>
  );
}

export function Toggle({ on, onChange, disabled, testId, label }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} aria-label={label} data-testid={testId} disabled={disabled}
      className={`k-toggle ${on ? "is-on" : ""}`} onClick={() => onChange(!on)}><i /></button>
  );
}

// Page-level views (underline tabs).
export function Segmented({ options, value, onChange, testIdPrefix = "seg" }) {
  return (
    <div className="k-seg" role="tablist">
      {options.map((o) => (
        <button key={o.key} type="button" role="tab" aria-selected={value === o.key} data-testid={`${testIdPrefix}-${o.key}`}
          className={value === o.key ? "is-on" : ""} onClick={() => onChange(o.key)}>
          {o.label}{o.count != null && <span className="k-seg-count">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

// Compact status filter chips (status: count).
export function Chips({ options, value, onChange }) {
  return (
    <div className="k-chips">
      {options.map((o) => (
        <button key={o.key} type="button" className={value === o.key ? "is-on" : ""} onClick={() => onChange(o.key)}>
          {o.label}{o.count != null && <span>{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Toolbar({ children }) {
  return <div className="k-toolbar">{children}</div>;
}

export function Callout({ tone = "blue", title, children, action }) {
  return (
    <div className={`k-callout is-${tone}`} role={tone === "red" ? "alert" : undefined}>
      <div style={{ minWidth: 0 }}>
        {title && <strong>{title}</strong>}
        {children && <div>{children}</div>}
      </div>
      {action}
    </div>
  );
}

export function Empty({ icon: Icon = Inbox, title = "Nothing here yet", children }) {
  return (
    <div className="k-empty">
      <span className="k-empty-icon"><Icon size={20} /></span>
      <strong>{title}</strong>
      {children && <p>{children}</p>}
    </div>
  );
}

export function Skeleton({ rows = 4 }) {
  return <div className="k-skel-wrap" aria-busy="true">{Array.from({ length: rows }).map((_, i) => <div key={i} className="k-skel" style={{ width: `${92 - i * 11}%` }} />)}</div>;
}

export function Avatar({ name = "", size = 30 }) {
  const ini = name.split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 360;
  return <span className="k-avatar" style={{ width: size, height: size, fontSize: size * 0.38, background: `hsl(${h} 70% 94%)`, color: `hsl(${h} 55% 35%)` }}>{ini}</span>;
}

// ─────────────────────────── table ───────────────────────────
// columns: [{ key, label, render(row), align: "right", width, hideSm }]. Below 760px rows become cards.
export function Table({ columns, rows, onRowClick, activeId, loading, empty, testId, rowKey = "id" }) {
  if (loading && !rows?.length) return <Skeleton rows={6} />;
  if (!rows?.length) return empty || <Empty />;
  return (
    <div className="k-table-wrap" data-testid={testId}>
      <table className={`k-table ${onRowClick ? "is-click" : ""}`}>
        <thead>
          <tr>{columns.map((c) => <th key={c.key} style={{ width: c.width, textAlign: c.align || "left" }} className={c.hideSm ? "k-hide-sm" : ""}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r[rowKey] ?? i} onClick={onRowClick ? () => onRowClick(r) : undefined} className={activeId && r[rowKey] === activeId ? "is-active" : ""}>
              {columns.map((c) => (
                <td key={c.key} data-label={c.label} style={{ textAlign: c.align || "left" }} className={c.hideSm ? "k-hide-sm" : ""}>
                  {c.render ? c.render(r) : r[c.key] ?? <span className="k-faint">—</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Cell({ main, sub, mono }) {
  return (
    <span className="k-cell">
      <span className={`k-cell-main ${mono ? "is-mono" : ""}`}>{main ?? <span className="k-faint">—</span>}</span>
      {sub && <span className="k-cell-sub">{sub}</span>}
    </span>
  );
}

export function Pagination({ page, pages, total, size, onPage, onSize, sizes = [10, 25, 50, 100] }) {
  if (!total) return null;
  const from = (page - 1) * size + 1;
  const to = Math.min(total, page * size);
  return (
    <div className="k-pager">
      <span className="k-pager-info">{from.toLocaleString("en-IN")}–{to.toLocaleString("en-IN")} of {total.toLocaleString("en-IN")}</span>
      <div className="k-pager-ctl">
        {onSize && (
          <select className="k-input k-select k-pager-size" value={size} onChange={(e) => onSize(Number(e.target.value))} aria-label="Rows per page">
            {sizes.map((s) => <option key={s} value={s}>{s} / page</option>)}
          </select>
        )}
        <button type="button" className="k-btn k-btn-secondary k-btn-sm k-btn-icon" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page"><ChevronLeft size={16} /></button>
        <span className="k-pager-page">{page} / {Math.max(1, pages || 1)}</span>
        <button type="button" className="k-btn k-btn-secondary k-btn-sm k-btn-icon" disabled={page >= (pages || 1)} onClick={() => onPage(page + 1)} aria-label="Next page"><ChevronRight size={16} /></button>
      </div>
    </div>
  );
}

// ─────────────────────────── overlays ───────────────────────────
function useEsc(fn) {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && fn?.();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [fn]);
}

export function Panel({ title, sub, onClose, actions, children, width = 560 }) {
  useEsc(onClose);
  return (
    <>
      <div className="k-overlay" onClick={onClose} />
      <aside className="k-panel" role="dialog" aria-modal="true" style={{ width: `min(${width}px, 100vw)` }}>
        <header className="k-panel-head">
          <div style={{ minWidth: 0 }}>
            <h3>{title}</h3>
            {sub && <p>{sub}</p>}
          </div>
          <div className="k-panel-actions">
            {actions}
            <button type="button" className="k-btn k-btn-secondary k-btn-sm k-btn-icon" onClick={onClose} aria-label="Close"><X size={16} /></button>
          </div>
        </header>
        <div className="k-panel-body">{children}</div>
      </aside>
    </>
  );
}

// Big summary block at the top of a Panel (amount/title + status + reference).
export function PanelHero({ kicker, title, meta, reference }) {
  return (
    <div className="k-hero">
      {kicker && <div className="k-hero-kicker">{kicker}</div>}
      <div className="k-hero-title">{title}</div>
      {meta && <div className="k-hero-meta">{meta}</div>}
      {reference && <div className="k-hero-ref"><span>{reference}</span><CopyBtn value={reference} /></div>}
    </div>
  );
}

export function Dialog({ title, onClose, children, footer }) {
  useEsc(onClose);
  return (
    <>
      <div className="k-overlay" onClick={onClose} />
      <div className="k-dialog" role="dialog" aria-modal="true">
        <header className="k-panel-head">
          <h3>{title}</h3>
          <button type="button" className="k-btn k-btn-secondary k-btn-sm k-btn-icon" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </header>
        <div className="k-dialog-body">{children}</div>
        {footer && <footer className="k-dialog-foot">{footer}</footer>}
      </div>
    </>
  );
}

export function Timeline({ events }) {
  if (!events?.length) return <div className="k-faint" style={{ fontSize: 13 }}>No events yet.</div>;
  return (
    <ol className="k-timeline">
      {events.map((e, i) => (
        <li key={i}>
          <span className="k-tl-msg">{e.message}</span>
          <span className="k-tl-at">{when(e.at, true)} · {ago(e.at)}</span>
        </li>
      ))}
    </ol>
  );
}

export function Code({ value }) {
  return <pre className="k-code">{typeof value === "string" ? value : JSON.stringify(value, null, 2)}</pre>;
}

// ─────────────────────────── styles ───────────────────────────
export function KitStyles() {
  return (
    <style>{`
      .k-page { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
      .k-grid { display: grid; gap: 14px; min-width: 0; }
      .k-grid-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .k-grid-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .k-grid-4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
      .k-faint { color: #8A90A1; }
      .k-mono, .is-mono { font-family: "JetBrains Mono", monospace; font-size: 12.5px; }

      .k-section { background: #fff; border: 1px solid #E4E7EF; border-radius: 14px; box-shadow: 0 1px 2px rgba(17,24,39,.04); min-width: 0; overflow: hidden; }
      .k-section-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 18px; border-bottom: 1px solid #EEF0F5; flex-wrap: wrap; }
      .k-section-title { display: flex; align-items: center; gap: 11px; min-width: 0; }
      .k-section-title h3 { margin: 0; font-size: 15px; font-weight: 700; color: #111827; letter-spacing: -0.01em; }
      .k-section-title p { margin: 2px 0 0; font-size: 12.5px; color: #5b6170; }
      .k-section-icon { width: 32px; height: 32px; flex: none; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; background: #ECEDFE; color: #2f33d6; }
      .k-section-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
      .k-section-body { padding: 16px 18px; }
      .k-section-foot { padding: 10px 18px; border-top: 1px solid #EEF0F5; background: #FAFBFD; }

      .k-kpi { text-align: left; font: inherit; color: inherit; background: #fff; border: 1px solid #E4E7EF; border-radius: 14px; padding: 15px 16px; display: flex; flex-direction: column; gap: 6px; min-width: 0; box-shadow: 0 1px 2px rgba(17,24,39,.04); }
      .k-kpi.is-click { cursor: pointer; transition: border-color .12s, box-shadow .12s; }
      .k-kpi.is-click:hover { border-color: #C9CCF7; box-shadow: 0 6px 18px -12px rgba(47,51,214,.45); }
      .k-kpi-top { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
      .k-kpi-label { font-size: 12.5px; font-weight: 500; color: #5b6170; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .k-kpi-icon { width: 28px; height: 28px; border-radius: 8px; flex: none; display: inline-flex; align-items: center; justify-content: center; background: #F2F3FA; color: #4b5263; }
      .k-kpi-value { font-size: 24px; font-weight: 700; letter-spacing: -0.02em; color: #111827; font-variant-numeric: tabular-nums; line-height: 1.15; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .k-kpi-sub { font-size: 12.5px; color: #5b6170; }
      .k-kpi.is-hero { border-color: transparent; color: #fff; background: radial-gradient(130% 150% at 0% 0%, #4c4ff0 0%, #2f33d6 52%, #1c1aa0 100%); box-shadow: 0 10px 28px -16px rgba(47,51,214,.75); }
      .k-kpi.is-hero .k-kpi-label, .k-kpi.is-hero .k-kpi-sub { color: rgba(255,255,255,.8); }
      .k-kpi.is-hero .k-kpi-value { color: #fff; }
      .k-kpi.is-hero .k-kpi-icon { background: rgba(255,255,255,.16); color: #fff; }

      .k-pill { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; padding: 3px 9px; border-radius: 100px; white-space: nowrap; line-height: 1.35; }
      .k-pill i { width: 6px; height: 6px; border-radius: 50%; }
      .k-health { display: inline-flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; }
      .k-health i { width: 8px; height: 8px; border-radius: 50%; }
      .k-health.is-ok { color: #0f7a45; } .k-health.is-ok i { background: #0f9d58; box-shadow: 0 0 0 3px rgba(15,157,88,.15); }
      .k-health.is-bad { color: #c5221f; } .k-health.is-bad i { background: #d93025; box-shadow: 0 0 0 3px rgba(217,48,37,.15); }

      .k-rows { display: flex; flex-direction: column; }
      .k-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 9px 0; border-bottom: 1px solid #F0F2F6; font-size: 13.5px; min-height: 22px; }
      .k-row:last-child { border-bottom: 0; }
      .k-row-k { color: #5b6170; flex: none; max-width: 48%; }
      .k-row-v { color: #111827; font-weight: 500; text-align: right; min-width: 0; display: inline-flex; align-items: center; justify-content: flex-end; gap: 6px; overflow-wrap: anywhere; }
      .k-row-v.is-mono { word-break: break-all; }
      .k-copy { border: 0; background: none; color: #8A90A1; cursor: pointer; padding: 3px; border-radius: 6px; display: inline-flex; flex: none; }
      .k-copy:hover { color: #2f33d6; background: #F2F3FA; }

      .k-btn { display: inline-flex; align-items: center; justify-content: center; gap: 7px; height: 38px; padding: 0 14px; border-radius: 10px; font: inherit; font-size: 13.5px; font-weight: 600; cursor: pointer; white-space: nowrap; border: 1px solid transparent; transition: background .12s, border-color .12s, color .12s; text-decoration: none; }
      .k-btn:disabled { opacity: .5; cursor: default; }
      .k-btn-sm { height: 32px; padding: 0 11px; font-size: 13px; border-radius: 9px; }
      .k-btn-icon { width: 32px; padding: 0; }
      .k-btn-primary { background: #2f33d6; color: #fff; box-shadow: 0 1px 2px rgba(47,51,214,.3); }
      .k-btn-primary:hover:not(:disabled) { background: #262ab8; }
      .k-btn-secondary { background: #fff; color: #1f2430; border-color: #DADEE8; }
      .k-btn-secondary:hover:not(:disabled) { background: #F6F7FB; border-color: #C9CEDB; }
      .k-btn-ghost { background: transparent; color: #4b5263; }
      .k-btn-ghost:hover:not(:disabled) { background: #F2F3FA; color: #111827; }
      .k-btn-danger { background: #fff; color: #c5221f; border-color: #F3C9C5; }
      .k-btn-danger:hover:not(:disabled) { background: #FDF2F1; }

      .k-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
      .k-field.is-wide { grid-column: 1 / -1; }
      .k-field-label { font-size: 12.5px; font-weight: 600; color: #374151; }
      .k-field-hint { font-size: 12px; color: #6b7280; }
      .k-input { width: 100%; box-sizing: border-box; height: 38px; padding: 0 12px; border: 1px solid #D5D9E3; border-radius: 10px; background: #fff; color: #111827; font: inherit; font-size: 14px; outline: none; transition: border-color .12s, box-shadow .12s; }
      .k-input:focus { border-color: #2f33d6; box-shadow: 0 0 0 3px rgba(47,51,214,.14); }
      .k-input::placeholder { color: #9aa0ae; }
      .k-input:disabled { background: #F6F7FA; color: #8A90A1; }
      .k-input.is-mono { font-family: "JetBrains Mono", monospace; font-size: 13px; }
      .k-textarea { height: auto; min-height: 120px; padding: 10px 12px; resize: vertical; line-height: 1.5; }
      .k-select { padding-right: 30px; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%235b6170' stroke-width='2.4'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 11px center; cursor: pointer; }
      .k-search { display: flex; align-items: center; gap: 8px; height: 38px; padding: 0 10px 0 12px; border: 1px solid #D5D9E3; border-radius: 10px; background: #fff; color: #8A90A1; min-width: 0; flex: 1 1 260px; transition: border-color .12s, box-shadow .12s; }
      .k-search:focus-within { border-color: #2f33d6; box-shadow: 0 0 0 3px rgba(47,51,214,.14); }
      .k-search input { flex: 1; min-width: 0; border: 0; outline: none; font: inherit; font-size: 14px; color: #111827; background: transparent; }
      .k-search button { border: 0; background: none; color: #8A90A1; cursor: pointer; display: inline-flex; padding: 2px; }

      .k-toggle { width: 40px; height: 22px; flex: none; border-radius: 100px; border: 0; background: #D5D9E3; position: relative; cursor: pointer; transition: background .15s; padding: 0; }
      .k-toggle i { position: absolute; top: 3px; left: 3px; width: 16px; height: 16px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.2); transition: transform .15s; }
      .k-toggle.is-on { background: #2f33d6; }
      .k-toggle.is-on i { transform: translateX(18px); }
      .k-toggle:disabled { opacity: .5; cursor: default; }

      .k-seg { display: flex; gap: 22px; border-bottom: 1px solid #E4E7EF; overflow-x: auto; scrollbar-width: none; }
      .k-seg::-webkit-scrollbar { display: none; }
      .k-seg button { flex: none; border: 0; background: none; padding: 10px 0 11px; font: inherit; font-size: 14px; font-weight: 500; color: #5b6170; cursor: pointer; border-bottom: 2px solid transparent; margin-bottom: -1px; display: inline-flex; align-items: center; gap: 7px; }
      .k-seg button:hover { color: #111827; }
      .k-seg button.is-on { color: #2f33d6; font-weight: 600; border-bottom-color: #2f33d6; }
      .k-seg-count { font-size: 11.5px; font-weight: 600; color: #5b6170; background: #F0F2F6; border-radius: 100px; padding: 1px 7px; }
      .k-seg button.is-on .k-seg-count { color: #2f33d6; background: #ECEDFE; }

      .k-chips { display: flex; gap: 6px; flex-wrap: wrap; }
      .k-chips button { display: inline-flex; align-items: center; gap: 6px; height: 30px; padding: 0 11px; border-radius: 100px; border: 1px solid #DADEE8; background: #fff; font: inherit; font-size: 12.5px; font-weight: 500; color: #4b5263; cursor: pointer; }
      .k-chips button span { font-size: 11.5px; color: #8A90A1; font-variant-numeric: tabular-nums; }
      .k-chips button.is-on { background: #111827; border-color: #111827; color: #fff; }
      .k-chips button.is-on span { color: rgba(255,255,255,.7); }

      .k-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 12px 18px; border-bottom: 1px solid #EEF0F5; }
      .k-toolbar .k-input { width: auto; }

      .k-callout { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 12px 14px; border-radius: 12px; font-size: 13.5px; line-height: 1.45; border: 1px solid; }
      .k-callout strong { display: block; margin-bottom: 2px; }
      .k-callout.is-blue { background: #F2F3FE; border-color: #DCDEFB; color: #23278f; }
      .k-callout.is-amber { background: #FFF8EC; border-color: #F6DDB0; color: #7a4600; }
      .k-callout.is-red { background: #FDF2F1; border-color: #F3C9C5; color: #9f1d1a; }
      .k-callout.is-green { background: #EFF8F3; border-color: #C9E8D6; color: #0b5c34; }

      .k-empty { display: flex; flex-direction: column; align-items: center; text-align: center; gap: 6px; padding: 36px 18px; color: #5b6170; }
      .k-empty strong { color: #111827; font-size: 14px; }
      .k-empty p { margin: 0; font-size: 13px; max-width: 360px; }
      .k-empty-icon { width: 42px; height: 42px; border-radius: 12px; background: #F2F3FA; color: #6b7280; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 4px; }

      .k-skel-wrap { display: flex; flex-direction: column; gap: 12px; padding: 18px; }
      .k-skel { height: 14px; border-radius: 6px; background: linear-gradient(90deg, #EEF0F5, #F7F8FB, #EEF0F5); background-size: 200% 100%; animation: kShimmer 1.2s linear infinite; }
      @keyframes kShimmer { from { background-position: 200% 0 } to { background-position: -200% 0 } }

      .k-avatar { display: inline-flex; align-items: center; justify-content: center; border-radius: 9px; font-weight: 700; flex: none; letter-spacing: .02em; }

      .k-table-wrap { width: 100%; overflow-x: auto; }
      .k-table { width: 100%; border-collapse: collapse; font-size: 13.5px; color: #111827; }
      .k-table th { font-size: 12px; font-weight: 600; color: #5b6170; padding: 10px 16px; background: #FAFBFD; border-bottom: 1px solid #E4E7EF; white-space: nowrap; position: sticky; top: 0; }
      .k-table td { padding: 11px 16px; border-bottom: 1px solid #F0F2F6; vertical-align: middle; font-variant-numeric: tabular-nums; }
      .k-table tbody tr:last-child td { border-bottom: 0; }
      .k-table.is-click tbody tr { cursor: pointer; }
      .k-table tbody tr:hover td { background: #FAFAFF; }
      .k-table tbody tr.is-active td { background: #F2F3FE; }
      .k-cell { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
      .k-cell-main { font-weight: 500; color: #111827; overflow-wrap: anywhere; }
      .k-cell-sub { font-size: 12px; color: #6b7280; overflow-wrap: anywhere; }

      .k-pager { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 16px; border-top: 1px solid #EEF0F5; flex-wrap: wrap; }
      .k-pager-info { font-size: 13px; color: #5b6170; font-variant-numeric: tabular-nums; }
      .k-pager-ctl { display: flex; align-items: center; gap: 8px; }
      .k-pager-size { width: auto; height: 32px; font-size: 13px; }
      .k-pager-page { font-size: 13px; color: #374151; min-width: 52px; text-align: center; font-variant-numeric: tabular-nums; }

      .k-overlay { position: fixed; inset: 0; background: rgba(17,24,39,.32); backdrop-filter: blur(1.5px); z-index: 200; animation: kFade .15s ease-out; }
      .k-panel { position: fixed; top: 0; right: 0; bottom: 0; background: #F5F6FA; z-index: 201; display: flex; flex-direction: column; box-shadow: -24px 0 60px -28px rgba(17,24,39,.35); animation: kSlide .18s ease-out; }
      .k-panel-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 18px; background: #fff; border-bottom: 1px solid #E4E7EF; }
      .k-panel-head h3 { margin: 0; font-size: 15.5px; font-weight: 700; color: #111827; }
      .k-panel-head p { margin: 2px 0 0; font-size: 12.5px; color: #5b6170; }
      .k-panel-actions { display: flex; align-items: center; gap: 8px; flex: none; }
      .k-panel-body { flex: 1; overflow-y: auto; padding: 16px 18px 40px; display: flex; flex-direction: column; gap: 12px; overscroll-behavior: contain; }
      .k-panel-body > * { flex: none; }
      .k-dialog { position: fixed; left: 50%; top: 50%; transform: translate(-50%, -50%); width: min(520px, calc(100vw - 24px)); max-height: calc(100vh - 40px); display: flex; flex-direction: column; background: #fff; border-radius: 16px; z-index: 202; box-shadow: 0 30px 80px -30px rgba(17,24,39,.5); overflow: hidden; }
      .k-dialog-body { padding: 18px; overflow-y: auto; display: flex; flex-direction: column; gap: 14px; }
      .k-dialog-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 18px; border-top: 1px solid #EEF0F5; background: #FAFBFD; }
      @keyframes kFade { from { opacity: 0 } to { opacity: 1 } }
      @keyframes kSlide { from { transform: translateX(24px); opacity: .6 } to { transform: none; opacity: 1 } }

      .k-hero { background: #fff; border: 1px solid #E4E7EF; border-radius: 14px; padding: 16px 18px; }
      .k-hero-kicker { font-size: 12.5px; font-weight: 600; color: #5b6170; }
      .k-hero-title { font-size: 28px; font-weight: 700; letter-spacing: -0.02em; color: #111827; margin-top: 4px; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
      .k-hero-meta { display: flex; align-items: center; gap: 10px; margin-top: 8px; font-size: 13px; color: #5b6170; flex-wrap: wrap; }
      .k-hero-ref { display: flex; align-items: center; gap: 6px; margin-top: 12px; padding-top: 12px; border-top: 1px solid #F0F2F6; font-family: "JetBrains Mono", monospace; font-size: 12.5px; color: #5b6170; min-width: 0; }
      .k-hero-ref > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

      .k-timeline { list-style: none; margin: 0; padding: 0; }
      .k-timeline li { position: relative; padding: 0 0 14px 22px; display: flex; flex-direction: column; gap: 2px; }
      .k-timeline li::before { content: ""; position: absolute; left: 4px; top: 6px; width: 9px; height: 9px; border-radius: 50%; background: #2f33d6; box-shadow: 0 0 0 3px #ECEDFE; }
      .k-timeline li::after { content: ""; position: absolute; left: 8px; top: 18px; bottom: 0; width: 1.5px; background: #E4E7EF; }
      .k-timeline li:last-child::after { display: none; }
      .k-tl-msg { font-size: 13.5px; font-weight: 500; color: #111827; }
      .k-tl-at { font-size: 12px; color: #8A90A1; }
      .k-code { margin: 0; padding: 12px 14px; background: #0f1222; color: #e5e7f3; border-radius: 10px; font-family: "JetBrains Mono", monospace; font-size: 12px; line-height: 1.55; overflow: auto; max-height: 360px; white-space: pre-wrap; word-break: break-word; }

      .k-form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
      .k-form-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .k-actions { display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
      .k-split { display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr); gap: 14px; align-items: start; }

      @media (max-width: 1200px) { .k-grid-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); } .k-split { grid-template-columns: minmax(0, 1fr); } }
      @media (max-width: 900px) { .k-grid-3, .k-form-3 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
      @media (max-width: 760px) {
        .k-grid-2, .k-grid-3, .k-form, .k-form-3 { grid-template-columns: minmax(0, 1fr); }
        .k-hide-sm { display: none; }
        .k-table thead { display: none; }
        .k-table, .k-table tbody, .k-table tr, .k-table td { display: block; width: 100%; box-sizing: border-box; }
        .k-table tr { padding: 10px 14px; border-bottom: 1px solid #EEF0F5; }
        .k-table td { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 5px 0; border: 0; text-align: right !important; }
        .k-table td::before { content: attr(data-label); font-size: 12px; color: #6b7280; text-align: left; flex: none; }
        .k-table td > * { min-width: 0; }
        .k-section-head, .k-section-body, .k-toolbar { padding-left: 14px; padding-right: 14px; }
      }
      @media (max-width: 480px) { .k-grid-4 { grid-template-columns: minmax(0, 1fr); } .k-kpi-value { font-size: 21px; } }
    `}</style>
  );
}
